import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  MapPin, Loader2, MessageCircle, Briefcase, Users, 
  Navigation, AlertCircle, RefreshCw, Locate 
} from "lucide-react";

const RADIUS_OPTIONS = [
  { value: "5", label: "5 km" },
  { value: "10", label: "10 km" },
  { value: "25", label: "25 km" },
  { value: "50", label: "50 km" },
];

const NearMePage = () => {
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [radius, setRadius] = useState("10");
  const [gettingLocation, setGettingLocation] = useState(false);
  useAuth(); // Ensure user is authenticated
  const navigate = useNavigate();

  // Get user's current GPS location
  const getCurrentLocation = useCallback(() => {
    setGettingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setGettingLocation(false);
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
        setGettingLocation(false);
        
        // Update user's location in backend (so others can find them)
        try {
          await api.post(`/users/update-location?lat=${latitude}&lng=${longitude}`);
        } catch (_error) {
          console.error("Failed to update location");
        }
        
        // Fetch nearby users
        fetchNearbyUsers(latitude, longitude, parseInt(radius));
      },
      (error) => {
        setGettingLocation(false);
        setLoading(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError("Location access required. Please enable location permissions in your browser settings to use this feature.");
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError("Location information is unavailable. Please try again.");
            break;
          case error.TIMEOUT:
            setLocationError("Location request timed out. Please try again.");
            break;
          default:
            setLocationError("An error occurred while getting your location. Please try again.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  }, [radius]);

  // Fetch nearby users based on GPS coordinates
  const fetchNearbyUsers = async (lat, lng, radiusKm) => {
    setLoading(true);
    try {
      const response = await api.get(`/users/nearby-gps?lat=${lat}&lng=${lng}&radius=${radiusKm}`);
      setNearbyUsers(response.data);
    } catch (_error) {
      toast.error("Failed to fetch nearby users");
      setNearbyUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial location fetch
  useEffect(() => {
    getCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when radius changes
  useEffect(() => {
    if (currentLocation) {
      fetchNearbyUsers(currentLocation.lat, currentLocation.lng, parseInt(radius));
    }
  }, [radius, currentLocation]);

  const handleRefresh = () => {
    getCurrentLocation();
  };

  const getInitials = (u) => {
    if (u.first_name) {
      return `${u.first_name[0]}${(u.last_name || "L")[0]}`.toUpperCase();
    }
    if (u.username) {
      return u.username.slice(0, 2).toUpperCase();
    }
    return u.phone?.slice(-2) || "??";
  };

  const handleStartChat = async (userId) => {
    try {
      const response = await api.post("/conversations", { participant_id: userId });
      navigate(`/messages/${response.data.id}`);
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="near-me-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <MapPin className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Near Me</h1>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={gettingLocation || loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${(gettingLocation || loading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Location Error State */}
        {locationError && (
          <Card className="mb-6 border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/50 mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-semibold text-red-700 dark:text-red-400 mb-2">Location Access Required</h3>
                <p className="text-sm text-red-600 dark:text-red-300 mb-4">{locationError}</p>
                <Button 
                  onClick={handleRefresh}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Locate className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Getting Location State */}
        {gettingLocation && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="p-3 rounded-full bg-primary/10 mb-4 animate-pulse">
                  <Navigation className="w-8 h-8 text-primary animate-bounce" />
                </div>
                <h3 className="font-semibold mb-2">Getting Your Location...</h3>
                <p className="text-sm text-muted-foreground">Please allow location access when prompted</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Location Found - Show Controls */}
        {currentLocation && !locationError && (
          <>
            {/* Location & Radius Selector */}
            <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                    <Navigation className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Live Location Active</p>
                    <p className="text-xs text-muted-foreground">
                      Using your current GPS location
                    </p>
                  </div>
                </div>
                
                {/* Radius Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Radius:</span>
                  <Select value={radius} onValueChange={setRadius}>
                    <SelectTrigger className="w-[100px]" data-testid="radius-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RADIUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}

            {/* Nearby Users */}
            {!loading && nearbyUsers.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                  {nearbyUsers.filter(u => u.location_type === "gps").length > 0 && (
                    <span>{nearbyUsers.filter(u => u.location_type === "gps").length} nearby within {radius} km</span>
                  )}
                  {nearbyUsers.filter(u => u.location_type === "city").length > 0 && (
                    <span>{nearbyUsers.filter(u => u.location_type === "gps").length > 0 ? " + " : ""}{nearbyUsers.filter(u => u.location_type === "city").length} in same city</span>
                  )}
                </p>
                {nearbyUsers.map((u) => (
                  <Card key={u.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar 
                          className="w-14 h-14 cursor-pointer"
                          onClick={() => navigate(`/user/${u.id}`)}
                        >
                          <AvatarImage src={u.avatar} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                            {getInitials(u)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => navigate(`/user/${u.id}`)}
                        >
                          <h3 className="font-semibold truncate">
                            {u.prefix && <span className="text-muted-foreground">{u.prefix}. </span>}
                            {u.first_name || u.username || "User"} {u.last_name || ""}
                          </h3>
                          
                          {/* Distance / Location Badge */}
                          {u.location_type === "gps" && u.distance_km !== undefined && (
                            <Badge variant="secondary" className="text-xs mb-1">
                              <MapPin className="w-3 h-3 mr-1" />
                              {u.distance_km < 1 
                                ? `${Math.round(u.distance_km * 1000)} m away` 
                                : `${u.distance_km} km away`
                              }
                            </Badge>
                          )}
                          {u.location_type === "city" && (
                            <Badge variant="outline" className="text-xs mb-1 border-blue-300 text-blue-600">
                              <MapPin className="w-3 h-3 mr-1" />
                              Same city
                            </Badge>
                          )}
                          
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                            {u.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-primary" /> {u.city}{u.state ? `, ${u.state}` : ''}
                              </span>
                            )}
                            {u.occupation && (
                              <span className="flex items-center gap-1 capitalize">
                                <Briefcase className="w-3 h-3" /> {u.occupation}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartChat(u.id)}
                            data-testid={`chat-btn-${u.id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => navigate(`/user/${u.id}`)}
                            data-testid={`view-btn-${u.id}`}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && nearbyUsers.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg mb-2">No people found within {radius} km</p>
                <p className="text-sm mb-4">
                  Try increasing the search radius or check back later
                </p>
                <div className="flex justify-center gap-2">
                  {parseInt(radius) < 50 && (
                    <Button 
                      variant="outline"
                      onClick={() => setRadius(radius === "5" ? "10" : radius === "10" ? "25" : "50")}
                    >
                      Increase Radius
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default NearMePage;
